import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Svg from 'svg-inline-react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { FR_PR_WHITE } from '@shared/styles/vars';
import FrCloseSvg from '@src/svgs/fr-close-small.svg';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { useAppDispatch } from '@src/redux';
import { removeBodyBackdrop, setBodyBackdrop } from '@src/redux/actions/bodyBackdrop';
import { useOpeningForConnectButtonLazyQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { OpeningsPopup } from '../Footer/OpeningsPopup';
import { ConnectParams } from '../interfaces/connectParams.interface';
import { DirectConnectProps } from './props';
import './styles.scss';

const MIN_MESSAGE_LENGTH = 80;

export const DirectConnect: FC<DirectConnectProps> = ({ isShown, pitchMessage, onClose, onConfirm, openingId }) => {
  const dispatch = useAppDispatch();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [connectParams, setConnectParams] = useState<ConnectParams>();

  const [fetchOpening] = useOpeningForConnectButtonLazyQuery({
    onCompleted: data => {
      setConnectParams({
        openingId: data.opening.id,
        openingTitle: data.opening.title,
        pitch: data.opening.pitch,
      });
    },
  });

  const removeBackdrop = () => {
    dispatch(removeBodyBackdrop());
  };

  const showBackdrop = () => {
    dispatch(setBodyBackdrop());
  };

  const showPopover = () => {
    showBackdrop();

    setIsPopoverOpen(true);
  };

  const closePopover = () => {
    removeBackdrop();

    setIsPopoverOpen(false);
  };

  const onOpeningSelect = async (connectParams: ConnectParams): Promise<void> => {
    setConnectParams(connectParams);

    closePopover();
  };

  const setMessage = (message: string) => {
    setConnectParams(prevState => {
      return {
        ...prevState,
        pitch: message,
      };
    });
  };

  useEffect(() => {
    const onKeyDown = () => {
      if (isPopoverOpen) {
        dispatch(removeBodyBackdrop());

        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    if (isShown) {
      textAreaRef.current?.focus();

      trackEvent({
        context: MetricContext.TALENT_AGENT_FEED,
        interaction: MetricAction.VIEW_DIRECT_CONNECT_SECTION,
      });
    }
  }, [isShown]);

  useEffect(() => {
    if (pitchMessage) {
      setMessage(pitchMessage);
    }
  }, [pitchMessage]);

  useEffect(() => {
    if (openingId) {
      fetchOpening({
        variables: {
          id: openingId,
        },
      });
    }
  }, [openingId]);

  const isConfirmButtonDisabled = (connectParams?.pitch?.trim()?.length || 0) < MIN_MESSAGE_LENGTH;

  return (
    <div className={classNames('talent-card-direct-connect', isShown && 'talent-card-direct-connect--shown')}>
      <div className="talent-card-direct-connect__field-label">Would you like to attach a job opening?</div>

      <Popover
        containerStyle={{ zIndex: '1000' }}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            arrowClassName="popover-arrow"
            arrowColor={FR_PR_WHITE}
            arrowSize={10}
            arrowStyle={{
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: `10px solid ${FR_PR_WHITE}`,
              bottom: '0.5rem',
              opacity: 1,
              position: 'absolute',
              right: '50%',
              zIndex: 21,
            }}
            childRect={childRect}
            popoverRect={popoverRect}
            position={position}
          >
            <OpeningsPopup
              onOpeningSelect={(openingId, openingTitle, pitch) => onOpeningSelect({ openingId, openingTitle, pitch })}
              onClose={() => closePopover()}
            />
          </ArrowContainer>
        )}
        isOpen={isPopoverOpen}
        onClickOutside={() => closePopover()}
        padding={10}
        positions={['top', 'left', 'right', 'bottom']}
        reposition={true}
      >
        <div className="talent-card-direct-connect__opening-field-wrapper" onClick={() => showPopover()}>
          <div
            className={classNames(
              'talent-card-direct-connect__opening-field',
              connectParams?.openingTitle && 'talent-card-direct-connect__opening-field--selected',
            )}
          >
            {connectParams?.openingTitle || 'No job selected'}
          </div>

          <div className="talent-card-direct-connect__opening-field-button">Select from existing</div>
        </div>
      </Popover>

      <div className="talent-card-direct-connect__field-label talent-card-direct-connect__field-label--top-padding">
        Your message
      </div>

      <div className="talent-card-direct-connect__input-wrapper">
        {isShown && (
          <div className="talent-card-direct-connect__input-container">
            <textarea
              autoFocus
              className="talent-card-direct-connect__textarea"
              onChange={event => setMessage(event.target.value)}
              placeholder="Invite the candidate to discuss your open position..."
              ref={textAreaRef}
              value={connectParams?.pitch || ''}
            />

            <span className="talent-card-direct-connect__input-limit">
              Your message needs to be at least {MIN_MESSAGE_LENGTH} characters. Current{' '}
              {connectParams?.pitch?.length || 0}/{MIN_MESSAGE_LENGTH}.
            </span>
          </div>
        )}
      </div>

      <div className="talent-card-direct-connect__footer">
        <div className="talent-card-direct-connect__close" onClick={onClose}>
          <Svg className="talent-card-direct-connect__close-icon" src={FrCloseSvg} />

          <span>Close</span>
        </div>

        <div
          className={classNames(
            'talent-card-direct-connect__footer-button',
            isConfirmButtonDisabled && 'talent-card-direct-connect__footer-button--disabled',
          )}
          onClick={() => {
            !isConfirmButtonDisabled && connectParams && onConfirm(connectParams);
          }}
        >
          Send message
        </div>
      </div>
    </div>
  );
};
